import { Dispatch, useMemo } from "react";
import { useDispatch } from "react-redux";

import {
  getAuthData,
  logoutNew,
  rotateToken,
  setIsAuthenticated,
  setIsBaseURLRetrieved,
  setIsError,
  signIn,
  signInCheck,
  signInCheckClear,
  signInPasswordless,
  signInPasswordlessFinish,
  signUpNew,
  signUpNewPasswordless,
  switchToEmail,
  switchToPhone,
} from "../redux/auth/actions";

import { setMonitorAlertsSettings } from "../redux/alerts/actions";
import { StatusSettings } from "../redux/alerts/index";
import {
  getAllDevicesStatuses,
  getDeviceStatuses,
  getDevices,
  getUserTagDevices,
  getVitalsUserNearby,
} from "../redux/devices/actions";
import {
  FlagsData,
  notifyLoginLogoutDTO,
  subscribeData,
  subscribeMobileDeviceDTO,
  unsubscribeData,
  unsubscribeMobileDeviceDTO,
  updateData,
} from "../redux/healthCheck";
import { GetFeedbackDTO, SetAlertTypeDTO } from "../redux/modalFeedback";
import {
  getFeedbackByAlertTypeRequest,
  setAlertByTypeRequest,
} from "../redux/modalFeedback/actions";
import { getRepeatAlert } from "../redux/repeatAlert/actions";

import { residentsReqOptions } from "../redux/residents";
import {
  getResidents,
  getResidentsPictures,
  getResidentsRulesAlertsStatuses,
} from "../redux/residents/action";

import {
  getAllNodesInfo,
  getNodeDetails,
  getOrganizationInfo,
  setOrganizationIdFromToken,
} from "../redux/organization/actions";

import {
  getUsers,
  getUsersMobileDevices,
  getUsersPicturesR,
} from "../redux/users/actions";

import {
  notificationAppear,
  notificationClear,
  notificationConfirm,
  notificationCongrat,
  notificationView,
  notificationViewFeedback,
} from "../redux/newNotification/actions";
import {
  changeUserLanguage,
  changeUserNotification,
  setUserIdFromToken,
  setUserRoleFromToken,
  userProfileChangeMonitorNodeId,
  userProfileChangeSelectedEndNodeId,
  userProfileChangeSelectedNodeId,
  userProfileRequest,
} from "../redux/userProfile/actions";

import {
  loginDTO,
  signInPasswordlessDTO,
  signInPasswordlessEndDTO,
  signUpDTO,
  signUpPasswordless,
} from "../redux/auth";
import {
  changeFeatureFlags,
  mainPageChanged,
  needToNotifyLogin,
  notifyLoginLogout,
  organizationServiceStatusChange,
  residentsServiceStatusChange,
  saveDevicePNInfo,
  setUserMobileDeviceAsTag,
  subscribeNotif,
  subscribeNotifFailure,
  subscribeNotifForbidden,
  subscribeUserMobileDeviceAsTag,
  unsubscribeNotif,
  unsubscribeUserMobileDeviceAsTag,
  updateSubscribeNotif,
} from "../redux/healthCheck/actions";

import { DeviceType, devicesReqOptions } from "../redux/devices";
import {
  getActivitiesFeedback,
  getOneResidentActivities,
  getResidentActivities,
  getResidentConfiguration,
  sendResidentActivity,
} from "../redux/residentActivityPlan/actions";
import { GetUsersDTO, RoleType } from "../redux/users";
import { SendActivityDTO } from "../redux/residentActivityPlan";
import { OneNotification } from "../redux/newNotification/types";
import {
  userActivityStart,
  userActivityStop,
} from "../redux/userActivity/actions";
import { notificationsSettings } from "../redux/userProfile";

export const Dispatcher = () => {
  const dispatcher = useDispatch();
  const rootDispatcher = useMemo(
    () => new RootDispatcher(dispatcher),
    [dispatcher]
  );
  return rootDispatcher;
};
export class RootDispatcher {
  private readonly dispatch: Dispatch<any>;

  constructor(dispatch: Dispatch<any>) {
    this.dispatch = dispatch;
  }
  setIsAuthenticated = (value: boolean): void =>
    this.dispatch(setIsAuthenticated(value));
  setIsError = (value: string): void => this.dispatch(setIsError(value));
  setIsBaseURLRetrieved = (value: boolean): void =>
    this.dispatch(setIsBaseURLRetrieved(value));
  signOut = (): void => {
    this.dispatch(logoutNew());
  };

  getUsersProfile = (withErrorMessage?: boolean): void =>
    this.dispatch(userProfileRequest(withErrorMessage));

  getFeedbackForAlert = (value: GetFeedbackDTO): void =>
    this.dispatch(getFeedbackByAlertTypeRequest(value));

  getOrganizationInfo = (blocking = true): void =>
    this.dispatch(getOrganizationInfo(blocking));
  getUsers = (value: GetUsersDTO): void => this.dispatch(getUsers(value));

  getNodeDetails = (nodeId: number): void =>
    this.dispatch(getNodeDetails(nodeId));
  changeSelectedNode = (nodeId: number): void =>
    this.dispatch(userProfileChangeSelectedNodeId(nodeId));
  changeSelectedEndNode = (nodeId: number): void =>
    this.dispatch(userProfileChangeSelectedEndNodeId(nodeId));
  changeMonitorNode = (nodeId: number): void =>
    this.dispatch(userProfileChangeMonitorNodeId(nodeId));

  languageChange = (value: string): void =>
    this.dispatch(changeUserLanguage(value));

  getResidents = (options?: residentsReqOptions, blocking = true): void =>
    this.dispatch(getResidents(options, blocking));
  getResidentsRulesAlertsStatuses = (): void =>
    this.dispatch(getResidentsRulesAlertsStatuses());

  getDevices = (options?: devicesReqOptions, blocking = true): void => {
    this.dispatch(getDevices(options, blocking));
    this.dispatch(getUserTagDevices(options, blocking));
  };
  getDevicesStatuses = (options: devicesReqOptions): void =>
    this.dispatch(getAllDevicesStatuses(options));
  getVitalsUserNearby = (): void => this.dispatch(getVitalsUserNearby());
  resolveAlert = (value: SetAlertTypeDTO): void =>
    this.dispatch(setAlertByTypeRequest(value));

  getDeviceStatuses = (deviceId: string, deviceType: DeviceType): void =>
    this.dispatch(getDeviceStatuses(deviceId, deviceType));
  setMonitorsAlertsSettings = (value: StatusSettings): void =>
    this.dispatch(setMonitorAlertsSettings(value));

  getAllNodesInfo = (rootId: number): void =>
    this.dispatch(getAllNodesInfo(rootId));

  subscribeNotification = (data: subscribeData): void =>
    this.dispatch(subscribeNotif(data));
  unsubscribeNotification = (data: unsubscribeData): void =>
    this.dispatch(unsubscribeNotif(data));
  updatePushNotification = (data: updateData): void =>
    this.dispatch(updateSubscribeNotif(data));

  notifyLoginLogout = (isLogin: boolean, value: notifyLoginLogoutDTO): void => {
    this.dispatch(notifyLoginLogout(isLogin, value));
  };
  needToNotifyLogin = (): void => {
    this.dispatch(needToNotifyLogin());
  };

  getRepeatAlert = (): void => this.dispatch(getRepeatAlert());

  subscribeForbidden = (deviceId: string): void =>
    this.dispatch(subscribeNotifForbidden(deviceId));
  subscribePostponed = (value: subscribeData): void =>
    this.dispatch(subscribeNotifFailure(value));
  saveDevicePNInfo = (value: subscribeData): void =>
    this.dispatch(saveDevicePNInfo(value));

  residentsServiceStatusChange = (isAlive: boolean): void =>
    this.dispatch(residentsServiceStatusChange(isAlive));
  organizationServiceStatusChange = (isAlive: boolean): void =>
    this.dispatch(organizationServiceStatusChange(isAlive));

  changeFeatureFlags = (value: FlagsData): void =>
    this.dispatch(changeFeatureFlags(value));

  getResidentsActivities = (startDate: string, endDate: string): void => {
    this.dispatch(getResidentActivities(startDate, endDate));
  };
  getOneResidentActivities = (
    startDate: string,
    endDate: string,
    residentId: string
  ): void => {
    this.dispatch(getOneResidentActivities(startDate, endDate, residentId));
  };
  getResidentsConfigurations = (): void => {
    this.dispatch(getResidentConfiguration());
  };

  mainPageChanged = (value: boolean): void => {
    this.dispatch(mainPageChanged(value));
  };

  getActivitiesFeedback = (): void => {
    this.dispatch(getActivitiesFeedback());
  };

  getResidentsPictures = (): void => {
    this.dispatch(getResidentsPictures());
  };

  getUsersPictures = (): void => {
    this.dispatch(getUsersPicturesR());
  };
  changeNotification = (userId: string, value: notificationsSettings): void => {
    this.dispatch(changeUserNotification(userId, value));
  };

  setOrganizationId = (value: number): void => {
    this.dispatch(setOrganizationIdFromToken(value));
  };

  setUserIdFromToken = (userId: string): void => {
    this.dispatch(setUserIdFromToken(userId));
  };
  setUserRoleFromToken = (role: RoleType): void => {
    this.dispatch(setUserRoleFromToken(role));
  };

  // new auth
  getAuthData = (): void => {
    this.dispatch(getAuthData());
  };
  rotateToken = (): void => {
    this.dispatch(rotateToken());
  };

  signIn = (dto: loginDTO): void => {
    this.dispatch(signIn(dto));
  };
  // signInSwitchToEmail = (): void => {
  //     this.dispatch(switchToEmail());
  // };
  // signInSwitchToPhone = (): void => {
  //     this.dispatch(switchToPhone());
  // };

  signUpNew = (dto: signUpDTO): void => {
    this.dispatch(signUpNew(dto));
  };
  signInSwitchToEmail = (): void => {
    this.dispatch(switchToEmail());
  };
  signInSwitchToPhone = (): void => {
    this.dispatch(switchToPhone());
  };
  signInCheck = (value: string): void => {
    this.dispatch(signInCheck(value));
  };
  signInCheckClear = (): void => {
    this.dispatch(signInCheckClear());
  };
  signInPasswordless = (dto: signInPasswordlessDTO): void => {
    this.dispatch(signInPasswordless(dto));
  };
  signInPasswordlessFinish = (dto: signInPasswordlessEndDTO): void => {
    this.dispatch(signInPasswordlessFinish(dto));
  };
  signUpNewPasswordless = (dto: signUpPasswordless): void => {
    this.dispatch(signUpNewPasswordless(dto));
  };

  getUsersMobileDevices = (): void => {
    this.dispatch(getUsersMobileDevices());
  };

  subscribeUserMobileDeviceAsTag = (value: subscribeMobileDeviceDTO): void => {
    this.dispatch(subscribeUserMobileDeviceAsTag(value));
  };

  unsubscribeUserMobileDeviceAsTag = (
    value: unsubscribeMobileDeviceDTO
  ): void => {
    this.dispatch(unsubscribeUserMobileDeviceAsTag(value));
  };
  setUserMobileDeviceAsTag = (): void => {
    this.dispatch(setUserMobileDeviceAsTag());
  };
  sendResidentActivity = (dto: SendActivityDTO): void => {
    this.dispatch(sendResidentActivity(dto));
  };

  notificationAppear = (value: OneNotification): void =>
    this.dispatch(notificationAppear(value));
  notificationView = (value: OneNotification): void =>
    this.dispatch(notificationView(value));
  notificationViewFeedback = (value: OneNotification): void =>
    this.dispatch(notificationViewFeedback(value));
  notificationConfirm = (notif: OneNotification): void => {
    this.dispatch(notificationConfirm(notif));
  };
  notificationCongrat = (value: OneNotification): void =>
    this.dispatch(notificationCongrat(value));
  clearNotification = (value: OneNotification): void =>
    this.dispatch(notificationClear(value));
  startSendingUserActivity = (): void => this.dispatch(userActivityStart());
  stopSendingUserActivity = (): void => this.dispatch(userActivityStop());
}
